import { useLazyQuery, useMutation } from '@apollo/client';
import { message } from 'antd';
import React, { FC } from 'react';
import {
  CasesCredentialVerificationV1CredCaseFileUploadButtonWithPreviewContainerCredentialVerificationCaseV1Document,
  CasesCredentialVerificationV1CredCaseFileUploadButtonWithPreviewContainerCredentialVerificationCaseV1ImageFileCreateAuthHeaderDocument,
  CasesCredentialVerificationV1CredCaseFileUploadButtonWithPreviewContainerCredentialVerificationCaseV1PdfFileCreateAuthHeaderDocument,
  FileInfo
} from '../../../generated';
import { AuthResult, AzureBlobResponse } from '../file-upload/azure-upload';
import { FileUploadButtonWithPreview } from './file-upload-button-with-preview';
interface CredCaseFileUploadButtonWithPreviewContainerProps {
  description?: string;
  caseId: string;
  title: string;
  buttonText: string;
  permittedContentTypes: string[];
  permittedExtensions: string[];
  isPreviewOnly?: boolean;
  setIsEditing?: React.Dispatch<React.SetStateAction<boolean>>;
  fieldName: string;
  fileName: string;
  onUploadSuccess: (response: AzureBlobResponse) => void; // after upload and fetch preview data success
  fileUrl?: React.Dispatch<React.SetStateAction<string | undefined>>;
  isShowFileName?: boolean;
  cropAllowed?: boolean; // whether to allow cropping of the file
}
export const CredCaseFileUploadButtonWithPreviewContainer: FC<CredCaseFileUploadButtonWithPreviewContainerProps> = ({cropAllowed=true, ...props}) => {
  // for upload file: start

  const [getAuthHeaderForPdfFile] = useMutation(
    CasesCredentialVerificationV1CredCaseFileUploadButtonWithPreviewContainerCredentialVerificationCaseV1PdfFileCreateAuthHeaderDocument
  );

  const [getAuthHeaderForImageFile] = useMutation(
    CasesCredentialVerificationV1CredCaseFileUploadButtonWithPreviewContainerCredentialVerificationCaseV1ImageFileCreateAuthHeaderDocument
  );

  const [getFilePreviewData] = useLazyQuery(CasesCredentialVerificationV1CredCaseFileUploadButtonWithPreviewContainerCredentialVerificationCaseV1Document, {
    fetchPolicy: 'network-only'
  });

  const handleUpdateAfterUploadSuccess = async (uploadedData: AzureBlobResponse) => {
    props.onUploadSuccess && props.onUploadSuccess(uploadedData);
  };

  const handleAuthorizeRequest = async (fileData: File, filename: string): Promise<AuthResult> => {
    if (fileData.type === 'application/pdf') {
      return getAuthHeaderForPdfFile({
        variables: {
          input: {
            caseId: props.caseId,
            fileName: filename,
            contentType: fileData.type,
            contentLength: fileData.size
          }
        }
      })
        .then((response) => {
          return {
            ...response?.data?.credentialVerificationCaseV1PdfFileCreateAuthHeader,
            ...{ isAuthorized: true }
          } as AuthResult;
        })
        .catch((error) => {
          console.error('Error uploading asset: ', error);
          message.error('Error getting authorization');
          return { isAuthorized: false } as AuthResult;
        });
    } else {
      return getAuthHeaderForImageFile({
        variables: {
          input: {
            caseId: props.caseId,
            fileName: filename,
            contentType: fileData.type,
            contentLength: fileData.size
          }
        }
      })
        .then((response) => {
          return {
            ...response?.data?.credentialVerificationCaseV1ImageFileCreateAuthHeader,
            ...{ isAuthorized: true }
          } as AuthResult;
        })
        .catch((error) => {
          console.error('Error uploading asset: ', error);
          message.error('Error getting authorization');
          return { isAuthorized: false } as AuthResult;
        });
    }
  };

  const handleFetchFilePreviewData = async (filename: string): Promise<FileInfo> => {
    const previewData = await getFilePreviewData({
      variables: {
        credentialVerificationCaseV1Id: props.caseId,
        fileName: filename
      }
    });
    if (props.fileUrl) {
      props.fileUrl(previewData?.data?.credentialVerificationCaseV1?.fileByFileName?.url);
    }
    return previewData?.data?.credentialVerificationCaseV1?.fileByFileName as FileInfo;
  };
  // for upload file: end

  return (
    <FileUploadButtonWithPreview
      title={props.title}
      buttonText={props.buttonText}
      permittedContentTypes={props.permittedContentTypes}
      permittedExtensions={props.permittedExtensions}
      isPreviewOnly={props.isPreviewOnly}
      setIsEditing={props.setIsEditing}
      onAuthorizeRequest={(fileData: File) => handleAuthorizeRequest(fileData, props.fileName)}
      fetchFilePreviewData={() => handleFetchFilePreviewData(props.fileName)}
      onUploadSuccess={(response) => handleUpdateAfterUploadSuccess(response)}
      isShowFileName={props.isShowFileName}
      cropAllowed={cropAllowed}
    />
  );
};
